<template>
    <teleport to="body">
        <div id="message-container">
            <transition-group name="fade" tag="div" @after-leave="updatePositions">
                <div v-for="msg in messages" :key="msg.id" class="ah-message" :class="`ah-message--${msg.type}`"
                    :style="{ top: `${msg.top}px` }" @click="removeMessage(msg.id)">
                    <!-- 图标 -->
                    <span class="message-icon" v-html="getIcon(msg.type)"></span>
                    <!-- 内容 -->
                    <span class="message-content">{{ msg.message }}</span>
                    <!-- 关闭按钮 -->
                    <i class="icon-close" @click.stop="removeMessage(msg.id)">×</i>
                </div>
            </transition-group>
        </div>
    </teleport>
</template>
  
<script>
let messageId = 0;

// ✅ 用于记录 3 秒内已显示的消息内容（去重）
const shownMessages = new Set();

export default {
    data() {
        return {
            messages: [], // 存储所有消息
        };
    },
    methods: {
        /**
         * 添加消息（防重复：3秒内相同内容只弹一次）
         * @param {string} message - 消息内容
         * @param {string} type - 消息类型：success, error, warning, info
         * @param {number} duration - 自动关闭时间（毫秒）
         */
        addMessage(message, type = "info", duration = 3000) {
            // ✅ 生成去重 key（仅按 message 内容）
            // 如果希望“相同内容但不同类型可同时弹”，请改为：
            // const key = `${type}::${message}`;
            const key = message;

            // 如果已在 3 秒内显示过，直接忽略
            if (shownMessages.has(key)) {
                return;
            }

            // 标记为已显示
            shownMessages.add(key);

            const id = messageId++;
            const top = this.messages.length * 60 + 20;
            const msg = { id, message, type, duration, top };

            this.messages.push(msg);

            // 自动关闭
            if (duration > 0) {
                setTimeout(() => {
                    this.removeMessage(id);
                }, duration);
            }
        },

        /**
         * 移除消息（手动或自动）
         * @param {number} id - 消息的唯一 ID
         */
        removeMessage(id) {
            const index = this.messages.findIndex((msg) => msg.id === id);
            if (index !== -1) {
                const msg = this.messages[index];
                const key = msg.message; // 与 addMessage 中保持一致
                shownMessages.delete(key); // ✅ 清除去重记录

                this.messages.splice(index, 1);
            }
        },

        /**
         * 更新剩余消息的 top 值（动画结束后调用）
         */
        updatePositions() {
            this.messages.forEach((msg, i) => {
                msg.top = i * 60 + 20;
            });
        },

        /**
         * 获取对应类型的图标
         * @param {string} type - 消息类型
         * @returns {string} - 图标 HTML
         */
        getIcon(type) {
            switch (type) {
                case "success":
                    return "<em style='font-size:15px;'>&#xE090;</em>";
                case "error":
                    return "<em style='font-size:15px;'>&#xE091;</em>";
                case "warning":
                    return "<em style='font-size:15px;'>&#xE136;</em>";
                case "info":
                default:
                    return "<em style='font-size:15px;'>&#xE030;</em>";
            }
        },
    },
};
</script>
  
<style scoped>
/* 容器样式 */
#message-container {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    pointer-events: none;
}

/* 单个消息框样式 */
.ah-message {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 10px;
    padding: 10px 20px;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    max-width: 300px;
    pointer-events: auto;
}

/* 图标样式 */
.ah-message .message-icon {
    margin-right: 10px;
    font-size: 20px;
    line-height: 1;
}

/* 内容样式 */
.ah-message .message-content {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 关闭按钮样式 */
.ah-message .icon-close {
    margin-left: 10px;
    font-style: normal;
    font-size: 16px;
    opacity: 0.7;
    transition: opacity 0.3s;
}

.ah-message .icon-close:hover {
    opacity: 1;
}

/* 不同类型的消息背景色 */
.ah-message--success {
    background-color: #67c23a;
}

.ah-message--error {
    background-color: #f56c6c;
}

.ah-message--warning {
    background-color: #e6a23c;
}

.ah-message--info {
    background-color: #909399;
}

/* 进入和离开动画 */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-20px) translateX(-50%);
}
</style>